import React, { useState, useEffect, useRef } from 'react';
import { 
  HiOutlineSquares2X2, 
  HiOutlineClipboardDocumentList,
  HiOutlineLightBulb,
  HiOutlineCheckBadge,
  HiOutlineBookOpen,
  HiOutlineMap,
  HiOutlineChartBar,
  HiOutlineExclamationTriangle,
  HiOutlineListBullet,
  HiOutlineDocumentText,
  HiOutlinePuzzlePiece,
  HiChevronDown,
  HiChevronRight,
  HiOutlineXMark,
  HiOutlineWrench,
  HiOutlineClipboardDocumentCheck,
  HiOutlineDocumentCheck,
  HiOutlineEye,
  HiOutlineShieldExclamation,
  HiOutlineMagnifyingGlass,
  HiOutlineShieldCheck,
  HiOutlineCheckCircle,
  HiOutlineCog,
} from 'react-icons/hi2';
import { adminService } from '../../services';
import { 
  MAIN_MENU_ITEMS_RAW, 
  SETTINGS_MENU_ITEMS_RAW,
  OEE_MENU_ITEMS_RAW,
  SPARE_PARTS_INVENTORY_MENU_ITEMS_RAW,
  RCA_MENU_ITEMS_RAW,
  CAPA_MENU_ITEMS_RAW,
  SAFETY_OBSERVATION_MENU_ITEMS_RAW,
  INCIDENT_MENU_ITEMS_RAW,
  HAZARD_MENU_ITEMS_RAW,
  REPORTS_DETAIL_ITEMS_RAW,
  PM_MENU_ITEMS_RAW,
  WORK_PERMIT_MENU_ITEMS_RAW 
} from '../../navigation/navConfig';
import { MAX_APPS_SEED } from '../../data/maxAppsDefinitions';
import {
  isLegacyNavAppHiddenFromTenantScope,
  normalizeTenantNavAppCodes,
} from '../../navigation/legacyNavAppScope';
import PageLoading from '../../components/common/PageLoading';
import SharedModal from '../../components/common/SharedModal';
import './ManageTenants.css';

const ALL_NAV_CONFIG_ITEMS = [
  ...MAIN_MENU_ITEMS_RAW,
  ...SETTINGS_MENU_ITEMS_RAW,
  ...OEE_MENU_ITEMS_RAW,
  ...SPARE_PARTS_INVENTORY_MENU_ITEMS_RAW,
  ...RCA_MENU_ITEMS_RAW,
  ...CAPA_MENU_ITEMS_RAW,
  ...SAFETY_OBSERVATION_MENU_ITEMS_RAW,
  ...INCIDENT_MENU_ITEMS_RAW,
  ...HAZARD_MENU_ITEMS_RAW,
  ...REPORTS_DETAIL_ITEMS_RAW,
  ...PM_MENU_ITEMS_RAW,
  ...WORK_PERMIT_MENU_ITEMS_RAW
].reduce((acc, item) => {
  if (item.key) acc[item.key] = item;
  return acc;
}, {});

const getSubModulesForApp = (appCode, fallbackJson, seedNavKeys) => {
  const map = {
    'CORE': MAIN_MENU_ITEMS_RAW,
    'OEE_PRODUCTION': OEE_MENU_ITEMS_RAW,
    'SPARE_INVENTORY': SPARE_PARTS_INVENTORY_MENU_ITEMS_RAW,
    'RCA_5WHY': RCA_MENU_ITEMS_RAW,
    'CAPA': CAPA_MENU_ITEMS_RAW,
    'SAFETY_OBS': SAFETY_OBSERVATION_MENU_ITEMS_RAW,
    'INCIDENT': INCIDENT_MENU_ITEMS_RAW,
    'HAZARD': HAZARD_MENU_ITEMS_RAW,
    'MACHINE_HEALTH': PM_MENU_ITEMS_RAW,
    'WORK_PERMIT': WORK_PERMIT_MENU_ITEMS_RAW,
  };
  
  if (map[appCode] && map[appCode].length > 0) {
    return map[appCode].map(item => item.key);
  }
  
  if (seedNavKeys && seedNavKeys.length > 0) {
    return seedNavKeys;
  }
  
  if (!fallbackJson) return [];
  try {
    return JSON.parse(fallbackJson);
  } catch {
    return [];
  }
};

const ManageTenants = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState(null);

  // App & Module management state
  const [showAppsModal, setShowAppsModal] = useState(false);
  const [allApps, setAllApps] = useState([]);
  const [tenantAppCodes, setTenantAppCodes] = useState([]);
  const [tenantNavKeys, setTenantNavKeys] = useState([]);
  const [expandedModules, setExpandedModules] = useState([]);
  const [savingApps, setSavingApps] = useState(false);
  const [loadingTenantNav, setLoadingTenantNav] = useState(false);
  /** Bumps when user opens another tenant’s modal; ignores stale getTenantNavApps responses. */
  const tenantNavLoadGenRef = useRef(0);

  useEffect(() => {
    loadTenants();
    loadAllApps();
  }, []);

  const loadAllApps = async () => {
    try {
      const data = await adminService.getNavApps();
      const apiApps = data || [];
      
      // Map of redundant/old codes to normalized standard codes
      const CODE_NORMALIZER = {
        'OEE': 'OEE_PRODUCTION',
        'PRODUCTION': 'OEE_PRODUCTION',
        'PRODUCTIVE': 'OEE_PRODUCTION',
        'PROD': 'OEE_PRODUCTION',
        'MAINTENANCE': 'MACHINE_HEALTH',
        'PM': 'MACHINE_HEALTH',
        'SPARES': 'SPARE_INVENTORY',
        'INVENTORY': 'SPARE_INVENTORY',
        'GENERAL_SETTINGS': 'SETTINGS',
        'CONFIG': 'SETTINGS'
      };

      const appMap = new Map();
      
      // 1. Initialize with Seeds
      MAX_APPS_SEED.forEach(seed => {
        appMap.set(seed.code, { ...seed });
      });
      
      // 2. Merge API data with normalization
      apiApps.forEach(apiApp => {
        const normalizedCode = CODE_NORMALIZER[apiApp.code] || apiApp.code;
        const existing = appMap.get(normalizedCode);
        
        if (existing) {
          // API is source of truth for names/categories (matches Feature apps + DB); seed fills gaps
          const merged = { ...existing, ...apiApp };
          if (!merged.name?.trim()) merged.name = existing.name;
          if (!merged.category?.trim()) merged.category = existing.category;
          if (!merged.iconEmoji) merged.iconEmoji = existing.iconEmoji;
          if (!merged.colorHex) merged.colorHex = existing.colorHex;
          if (!merged.description?.trim()) merged.description = existing.description;
          if (merged.navKeysJson == null || merged.navKeysJson === '') merged.navKeysJson = existing.navKeysJson;
          if (merged.navKeys == null) merged.navKeys = existing.navKeys;
          appMap.set(normalizedCode, merged);
        } else {
          // Brand new app not in seed and not an alias
          appMap.set(normalizedCode, apiApp);
        }
      });
      
      const mergedApps = Array.from(appMap.values());
      const filteredApps = mergedApps.filter(
        (app) => !isLegacyNavAppHiddenFromTenantScope(app.code),
      );
      
      setAllApps(filteredApps);
    } catch (err) {
      console.error('Error loading nav apps:', err);
      setAllApps(MAX_APPS_SEED);
    }
  };

  const loadTenants = async () => {
    try {
      setLoading(true);
      const data = await adminService.getAllTenants();
      setTenants(data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load tenants');
      console.error('Error loading tenants:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleManageApps = async (tenant) => {
    const loadGen = ++tenantNavLoadGenRef.current;
    
    // Optimistically set data if tenant already has it from table list
    setSelectedTenant(tenant);
    setTenantAppCodes(normalizeTenantNavAppCodes(tenant.appCodes ?? []));
    setTenantNavKeys(tenant.navKeys ?? []);
    
    setExpandedModules([]);
    setLoadingTenantNav(true);
    setShowAppsModal(true);

    try {
      const data = await adminService.getTenantNavApps(tenant.tenantId);
      if (loadGen !== tenantNavLoadGenRef.current) return;
      
      // Update with fresh data from server
      setTenantAppCodes(normalizeTenantNavAppCodes(data?.appCodes ?? []));
      setTenantNavKeys(data?.navKeys ?? []);
    } catch (err) {
      if (loadGen !== tenantNavLoadGenRef.current) return;
      console.error('Error loading tenant apps:', err);
    } finally {
      if (loadGen === tenantNavLoadGenRef.current) {
        setLoadingTenantNav(false);
      }
    }
  };

  const handleToggleApp = (code, navKeysJson) => {
    setTenantAppCodes(prev => {
      const isCurrentlyEnabled = prev.includes(code);
      
      if (isCurrentlyEnabled) {
        // Disabling parent -> remove all its sub-modules
        const keysToRemove = getSubModulesForApp(code, navKeysJson, null);
        setTenantNavKeys(prevKeys => prevKeys.filter(k => !keysToRemove.includes(k)));
        return prev.filter(c => c !== code);
      } else {
        // Enabling parent -> enable all sub-modules automatically for convenience
        const keysToAdd = getSubModulesForApp(code, navKeysJson, null);
        setTenantNavKeys(prevKeys => {
          const newKeys = new Set([...prevKeys, ...keysToAdd]);
          return Array.from(newKeys);
        });
        return [...prev, code];
      }
    });
  };

  const handleToggleNavKey = (key, appCode) => {
    setTenantNavKeys(prev => {
      const isCurrentlyEnabled = prev.includes(key);
      
      if (isCurrentlyEnabled) {
        return prev.filter(k => k !== key);
      } else {
        // Enabling sub-module -> ensure parent is enabled
        setTenantAppCodes(prevApps => {
          if (!prevApps.includes(appCode)) {
            return [...prevApps, appCode];
          }
          return prevApps;
        });
        return [...prev, key];
      }
    });
  };

  const toggleExpandModule = (code) => {
    setExpandedModules(prev => 
      prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]
    );
  };

  const handleSelectAll = (selectAll) => {
    if (!selectAll) {
      setTenantAppCodes([]);
      setTenantNavKeys([]);
      return;
    }

    const newAppCodes = new Set();
    const newNavKeys = new Set();
    
    allApps.forEach(app => {
      newAppCodes.add(app.code);
      const keys = getSubModulesForApp(app.code, app.navKeysJson, app.navKeys);
      keys.forEach(k => newNavKeys.add(k));
    });

    setTenantAppCodes(Array.from(newAppCodes));
    setTenantNavKeys(Array.from(newNavKeys));
  };

  const handleUpdateTenantApps = async () => {
    try {
      setSavingApps(true);
      await adminService.updateTenantNavApps(selectedTenant.tenantId, {
        appCodes: tenantAppCodes,
        navKeys: tenantNavKeys
      });

      const currentTenantId =
        typeof window !== 'undefined' ? localStorage.getItem('tenantId') : null;
      const isEditingLoggedInTenant =
        currentTenantId && parseInt(currentTenantId, 10) === selectedTenant.tenantId;

      // Only touch session nav cache / local tenant when the saved tenant is the one in this browser session
      if (typeof window !== 'undefined' && isEditingLoggedInTenant) {
        try {
          const tenantStr = localStorage.getItem('tenant');
          if (tenantStr) {
            const tenantObj = JSON.parse(tenantStr);
            tenantObj.appCodes = tenantAppCodes;
            tenantObj.subscribedModules = tenantAppCodes;
            localStorage.setItem('tenant', JSON.stringify(tenantObj));
          }
        } catch (e) {
          console.error('Failed to update local storage tenant:', e);
        }
        sessionStorage.removeItem('pm_nav_scope_v2');
        window.dispatchEvent(new CustomEvent('pm_nav_scope_refresh'));
      }

      setShowAppsModal(false);
      loadTenants();
      alert('Modules updated successfully for ' + selectedTenant.tenantName);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update modules');
      console.error('Error updating tenant apps:', err);
    } finally {
      setSavingApps(false);
    }
  };

  const getNavKeyIcon = (key) => {
    const k = key.toLowerCase();
    if (k.includes('work-order') || k.includes('task')) return <HiOutlineClipboardDocumentList />;
    if (k.includes('kaizen') || k.includes('idea')) return <HiOutlineLightBulb />;
    if (k.includes('audit') || k.includes('5s')) return <HiOutlineCheckBadge />;
    if (k.includes('opl') || k.includes('manual')) return <HiOutlineBookOpen />;
    if (k.includes('flowchart') || k.includes('map')) return <HiOutlineMap />;
    if (k.includes('dashboard') || k.includes('chart')) return <HiOutlineChartBar />;
    if (k.includes('breakdown') || k.includes('failure')) return <HiOutlineExclamationTriangle />;
    if (k.includes('reason') || k.includes('cause')) return <HiOutlineListBullet />;
    if (k.includes('report') || k.includes('doc')) return <HiOutlineDocumentText />;
    return <HiOutlinePuzzlePiece />;
  };

  const getAppIcon = (code) => {
    switch (code) {
      case 'MACHINE_HEALTH': return <HiOutlineWrench />;
      case 'WORK_ORDER': return <HiOutlineClipboardDocumentCheck />;
      case 'WORK_PERMIT': return <HiOutlineDocumentCheck />;
      case 'SPARE_INVENTORY': return <HiOutlineChartBar />;
      case 'SAFETY_OBS': return <HiOutlineEye />;
      case 'HAZARD': return <HiOutlineClipboardDocumentList />;
      case 'INCIDENT': return <HiOutlineShieldExclamation />;
      case 'KAIZEN': return <HiOutlineLightBulb />;
      case 'RCA_5WHY': return <HiOutlineMagnifyingGlass />;
      case 'CAPA': return <HiOutlineShieldCheck />;
      case 'QUALITY_5S': return <HiOutlineCheckCircle />;
      case 'OPL': return <HiOutlineBookOpen />;
      case 'FLOWCHART': return <HiOutlineMap />;
      case 'OEE_PRODUCTION': return <HiOutlineChartBar />;
      case 'REPORTS': return <HiOutlineChartBar />;
      case 'SETTINGS': return <HiOutlineCog />;
      default: return <HiOutlineSquares2X2 />;
    }
  };



  if (loading) {
    return (
      <div className="manage-tenants-container">
        <PageLoading message="Loading tenants…" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="manage-tenants-container">
        <div className="error">{error}</div>
      </div>
    );
  }

  return (
    <div className="manage-tenants-container">
      <div className="tenants-header">
        <h2>Manage Tenants</h2>
        <p>View and manage all tenant subscriptions</p>
      </div>

      <div className="tenants-table-container">
        <table className="tenants-table">
          <thead>
            <tr>
              <th>Tenant Code</th>
              <th>Tenant Name</th>
              <th>Contact Person</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tenants.length === 0 ? (
              <tr>
                <td colSpan="10" className="no-data">No tenants found</td>
              </tr>
            ) : (
              tenants.map((tenant) => (
                <tr key={tenant.tenantId}>
                  <td>{tenant.tenantCode}</td>
                  <td>{tenant.tenantName}</td>
                  <td>{tenant.contactPerson || 'N/A'}</td>
                  <td>{tenant.email || 'N/A'}</td>
                  <td>{tenant.phone || 'N/A'}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-action-secondary"
                        onClick={() => handleManageApps(tenant)}
                        title="Manage Modules"
                      >
                        <HiOutlineSquares2X2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Manage Modules Modal */}
      {selectedTenant && (
        <SharedModal
          isOpen={showAppsModal}
          onClose={() => setShowAppsModal(false)}
          customWidth="900px"
          title={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: '2rem' }}>
              <div className="header-content">
                <h3 id="apps-modal-title" style={{ margin: 0 }}>Manage Modules</h3>
                <p className="modal-subtitle" style={{ margin: '4px 0 0' }}>
                  {selectedTenant.tenantName} ({selectedTenant.tenantCode})
                </p>
              </div>
              <div className="header-actions" style={{ margin: 0, marginRight: '8px' }}>
                <button
                  type="button"
                  className="btn-bulk-select"
                  disabled={loadingTenantNav}
                  onClick={() => handleSelectAll(true)}
                >
                  Select All
                </button>
                <div className="action-divider" aria-hidden />
                <button
                  type="button"
                  className="btn-bulk-deselect"
                  disabled={loadingTenantNav}
                  onClick={() => handleSelectAll(false)}
                >
                  Deselect All
                </button>
              </div>
            </div>
          }
          footer={
            <div className="apps-modal-footer-ui">
              <div className="selection-summary">
                <strong>{tenantAppCodes.length}</strong> modules enabled
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button type="button" className="btn-secondary" onClick={() => setShowAppsModal(false)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleUpdateTenantApps}
                  disabled={savingApps || loadingTenantNav}
                  style={{ minWidth: '120px' }}
                >
                  {savingApps ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          }
        >
          <div className="modules-hierarchical-list" style={{ padding: '0', background: 'transparent' }}>
            {Object.entries(
              allApps.reduce((acc, app) => {
                const cat = app.category || 'Other';
                if (!acc[cat]) acc[cat] = [];
                acc[cat].push(app);
                return acc;
              }, {})
            ).map(([category, apps]) => (
              <div key={category} className="module-group">
                <h4 className="group-title">{category}</h4>
                <div className="group-list">
                  {apps.map((app) => {
                    const allKeys = getSubModulesForApp(app.code, app.navKeysJson, app.navKeys);
                    const hasSubModules = allKeys.length > 0;
                    const isExpanded = expandedModules.includes(app.code);
                    const isAppEnabled = tenantAppCodes.includes(app.code);

                    return (
                      <div key={app.code} className="parent-module-container">
                        <div className={`module-row ${isAppEnabled ? 'active' : ''}`}>
                          <div className="module-info">
                            {hasSubModules ? (
                              <div className="module-expand-toggle" onClick={() => toggleExpandModule(app.code)}>
                                {isExpanded ? <HiChevronDown /> : <HiChevronRight />}
                              </div>
                            ) : (
                              <div className="module-expand-spacer" />
                            )}
                            <div className="module-icon">
                              {getAppIcon(app.code)}
                            </div>
                            <div 
                              className="module-text" 
                              onClick={hasSubModules ? () => toggleExpandModule(app.code) : undefined} 
                              style={{ cursor: hasSubModules ? 'pointer' : 'default' }}
                            >
                              <span className="module-name">{app.name}</span>
                              <span className="module-code">{app.code}</span>
                            </div>
                          </div>
                          <div className="module-toggle">
                            <label className="switch">
                              <input 
                                type="checkbox" 
                                checked={isAppEnabled}
                                onChange={() => handleToggleApp(app.code, app.navKeysJson)}
                              />
                              <span className="slider round"></span>
                            </label>
                          </div>
                        </div>

                        {hasSubModules && (
                          <div className={`sub-module-list ${isExpanded ? 'expanded' : ''}`}>
                            <div className="sub-module-list-items">
                              {allKeys.map(key => {
                                const configItem = ALL_NAV_CONFIG_ITEMS[key];
                                const isSelected = tenantNavKeys.includes(key);
                                const IconComponent = configItem?.icon || getNavKeyIcon(key).type;
                                const label = configItem?.label || key.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

                                return (
                                  <div 
                                    key={key} 
                                    className={`sub-module-row-item ${isSelected ? 'active' : ''}`}
                                    onClick={() => handleToggleNavKey(key, app.code)}
                                  >
                                    <div className="sub-module-row-left">
                                      <div className="sub-module-icon-box">
                                        <IconComponent />
                                      </div>
                                      <div className="sub-module-row-content">
                                        <span className="sub-module-row-label">{label}</span>
                                        <span className="sub-module-row-badge">{key}</span>
                                      </div>
                                    </div>
                                    <div className="sub-module-row-toggle">
                                      <label className="switch mini" onClick={(e) => e.stopPropagation()}>
                                        <input 
                                          type="checkbox" 
                                          checked={isSelected}
                                          onChange={() => handleToggleNavKey(key, app.code)}
                                        />
                                        <span className="slider round"></span>
                                      </label>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </SharedModal>
      )}

    </div>
  );
};

export default ManageTenants;

